import { Field } from 'formik';
import { memo, PropsWithChildren } from 'react';

import {
  ActionTableInput,
  ConditionTableInput,
  MultipleInput,
  MultipleSelectCard,
  MultipleSelectInput,
  RadioTableInput,
  Responsive,
  SelectInput,
  Text,
  TextAreaInput,
  TextInput,
  ToggleButton,
} from '@components';
import { columnDirection, columnSpace, InputMod, InputType } from '@enums';
import type { FormFieldProps } from '@types';
import { cn } from '@utils';

export const FormField = ({
  withoutLabel,
  color = 'text-gray-700',
  inputType = InputType.Text,
  direction = columnDirection.Column,
  description = '',
  fullWidth,
  name,
  emptyData,
  format,
  centerItem = false,
  withoutClear = false,
  columnPercentage = columnSpace.TwoOverFive,
  className = '',
  textInputClassName = '',
  defaultValue = '',
  children,
  inputMod,
  label,
  besideTitle,
  required,
  mobileTextInput = 'flex-col',
  ...props
}: PropsWithChildren<FormFieldProps>) => {
  const getInput = () => {
    switch (inputType) {
      case InputType.Select:
        return (
          <Field
            name={name}
            label={label}
            format={format}
            className={textInputClassName}
            inputMod={inputMod}
            fullWidth={fullWidth}
            {...props}
            as={SelectInput}
          />
        );
      case InputType.Area:
        return <Field name={name} className={textInputClassName} {...props} as={TextAreaInput} />;
      case InputType.Switch:
        return (
          <Field
            inputMod={inputMod}
            label={label}
            className={textInputClassName}
            name={name}
            description={description}
            {...props}
            as={ToggleButton}
          />
        );
      case InputType.MultipleSelect:
        return <Field name={name} format={format} fullWidth={fullWidth} {...props} as={MultipleSelectCard} />;
      case InputType.MultipleSelectInput:
        return (
          <Field
            name={name}
            label={label}
            format={format}
            className={textInputClassName}
            inputMod={inputMod}
            fullWidth={fullWidth}
            {...props}
            as={MultipleSelectInput}
          />
        );
      case InputType.Table:
        if (name === 'actions') {
          return (
            <ActionTableInput
              className={textInputClassName}
              format={format}
              name={name}
              columnPercentage={columnPercentage}
              emptyData={emptyData}
            />
          );
        } else {
          return (
            <ConditionTableInput
              className={textInputClassName}
              format={format}
              name={name}
              columnPercentage={columnPercentage}
              emptyData={emptyData}
            />
          );
        }

      case InputType.Radio:
        return <RadioTableInput name={name} />;
      case InputType.Custom:
        return children;
      case InputType.MultipleInput:
        return (
          <Field
            {...props}
            name={name}
            className={textInputClassName}
            defaultValue={defaultValue}
            emptyData={emptyData}
            label={label}
            fullWidth={fullWidth}
            inputMod={inputMod}
            withoutClear={withoutClear}
            value={undefined}
            as={MultipleInput}
          />
        );
      case InputType.Password:
        return (
          <Field
            name={name}
            type="password"
            className={textInputClassName}
            defaultValue={defaultValue}
            label={label}
            fullWidth={fullWidth}
            inputMod={inputMod}
            withoutClear={withoutClear}
            {...props}
            as={TextInput}
          />
        );
      default:
        return (
          <Field
            name={name}
            className={textInputClassName}
            defaultValue={defaultValue}
            label={label}
            fullWidth={fullWidth}
            inputMod={inputMod}
            withoutClear={withoutClear}
            {...props}
            as={TextInput}
          />
        );
    }
  };
  const isColumn = direction === columnDirection.Column;
  const isRow = direction === columnDirection.Row;
  const isColumnRow = direction === columnDirection.ColumnRow;

  const isTwoOverFive = columnPercentage === columnSpace.TwoOverFive;
  const isThreeOverFive = columnPercentage === columnSpace.ThreeOverFive;
  const isHalf = columnPercentage === columnSpace.Half;

  const isTable = inputType === InputType.Table;
  const isFilled = inputMod === InputMod.Filled;

  const dynamicContainerClasses = cn(
    'gap-3',
    fullWidth && 'w-full',
    (isRow || isColumnRow) && 'flex',
    isColumn && !centerItem && !isHalf && 'grid grid-cols-1',
    className,
  );

  const labelContainerClasses = cn(
    'pr-10 flex flex-col space-y-3',
    (isRow || isColumnRow || isTable) && isTwoOverFive && !withoutLabel && 'w-2/5',
    (isRow || isColumnRow || isTable) && isThreeOverFive && !withoutLabel && 'w-3/5',
    (isRow || isColumnRow || isTable) && isHalf && !withoutLabel && 'w-2/4',
  );

  const InputContainerClasses = cn(
    'flex flex-col gap-y-3',
    fullWidth && !(isRow || isColumnRow) && 'w-full',
    (isRow || isColumnRow || isTable) && isTwoOverFive && !withoutLabel && 'w-3/5',
    (isRow || isColumnRow || isTable) && isThreeOverFive && !withoutLabel && 'w-2/5',
    (isRow || isColumnRow || isTable) && isHalf && !withoutLabel && 'w-2/4',
    centerItem && 'items-center justify-center',
  );

  const RenderLabel = memo(({ value, level = 5, required = false, color, renderLabelClassname = '' }: any) =>
    value && typeof value === 'string' ? (
      <Text $level={level} color={color} className={cn('font-normal', renderLabelClassname)}>
        {`${value} ${required ? '*' : ''}`}
      </Text>
    ) : (
      value
    ),
  );

  if (!name) return;
  return (
    <div className={dynamicContainerClasses}>
      <Responsive showAbove="sm">
        {withoutLabel && isRow && isTwoOverFive && <div className="w-2/5"></div>}
        {!withoutLabel && (
          <div className={labelContainerClasses}>
            <RenderLabel value={label} required={required} color={color} renderLabelClassname="text-left leading-6" />
            {isColumnRow && !isFilled && (
              <RenderLabel
                value={description}
                required={required}
                color={color}
                renderLabelClassname="text-justify leading-6"
              />
            )}
          </div>
        )}
        <div className={InputContainerClasses}>
          {getInput()}

          {!isColumnRow && !isFilled && (
            <RenderLabel
              value={description}
              required={required}
              color={color}
              renderLabelClassname="text-justify leading-6"
            />
          )}
        </div>
      </Responsive>
      <Responsive showBelow="sm">
        <div className={`${mobileTextInput} flex flex-1 gap-3`}>
          {!withoutLabel && besideTitle && (
            <div className="flex justify-between items-center gap-3">
              <RenderLabel value={label} required={required} color={color} renderLabelClassname="text-left leading-6" />
              {getInput()}
            </div>
          )}

          {!isFilled && !besideTitle && !withoutLabel && (
            <RenderLabel
              value={label}
              required={required}
              color={color}
              renderLabelClassname="text-justify leading-6"
            />
          )}
          {!besideTitle && <div className={mobileTextInput}>{getInput()}</div>}
        </div>
      </Responsive>
    </div>
  );
};
